import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function StepItem({ completed, title, description }) {
  return (
    <div className="flex gap-3 py-2.5">
      {completed ? (
        <CheckCircleIcon sx={{ fontSize: 20, color: '#10B981' }} className="flex-shrink-0 mt-0.5" />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#D6D3D1' }} className="flex-shrink-0 mt-0.5" />
      )}
      <div className="flex flex-col">
        <h2 className={`text-sm font-medium ${completed ? 'text-surface-500' : 'text-surface-800'}`}>{title}</h2>
        <p className="text-xs text-surface-500">{description}</p>
      </div>
    </div>
  );
}

// curated list of integrations to showcase
const SHOWCASE_INTEGRATIONS = [
  'shopify', 'amazon', 'shipbob', 'shiphero', 'shipstation',
  'bluebox', 'flexport', 'extensiv-3pl-central', 'flowspace', 'gobolt',
  'deposco', 'logiwa', 'skuvault', 'infoplus', 'mintsoft',
  'linnworks', 'packiyo', 'pulpo', 'shipfusion', 'stord',
];

function HomepageWelcome({ connectionData, apiKeys }) {
  /* eslint-disable-next-line camelcase */
  const sandboxGenerated = connectionData?.some(({ integration_name }) => integration_name === 'sandbox');
  /* eslint-disable-next-line camelcase */
  const linkSetup = connectionData?.some(({ integration_name }) => integration_name !== 'sandbox');
  /* eslint-disable-next-line camelcase */
  const prodApiCall = connectionData?.some(({ integration_name, times_used }) => integration_name !== 'sandbox' && times_used > 0);

  const completedSteps = [apiKeys, sandboxGenerated, linkSetup, prodApiCall].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* Getting Started Card */}
      <div className="card p-5 flex gap-6 flex-shrink-0">
        {/* Left: Title, description, button, logo marquee */}
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-lg font-semibold text-surface-900">Getting Started</h1>
            <span className="text-xs font-medium text-surface-400 bg-surface-100 px-2 py-0.5 rounded-md">
              {`${completedSteps}/4`}
            </span>
          </div>
          <p className="text-sm text-surface-500 mb-4">
            Complete these steps to unlock the full power of Trackstar.
          </p>
          <a
            href="https://docs.trackstarhq.com/how-to-guides/getting-started"
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary w-fit mb-4"
          >
            <span>Complete Setup</span>
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </a>

          {/* Horizontal scrolling integration logos */}
          <div className="h-12 overflow-hidden relative mt-8">
            <div
              className="flex"
              style={{
                animation: 'scrollHorizontal 30s linear infinite',
                willChange: 'transform',
              }}
            >
              {/* Two identical sets for seamless loop */}
              {['a', 'b'].map((group) => (
                <div key={group} className="flex flex-shrink-0" style={{ gap: '12px', paddingRight: '12px' }}>
                  {SHOWCASE_INTEGRATIONS.map((name) => (
                    <div
                      key={`${group}-${name}`}
                      className="w-10 h-10 rounded-lg bg-surface-50 border border-surface-100 flex items-center justify-center p-1.5 flex-shrink-0"
                    >
                      <img
                        src={`https://trackstarlogosbucket.s3.amazonaws.com/${name}.png`}
                        alt={name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {/* Fade edges */}
            <div className="absolute top-0 left-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none" />
            <div className="absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
          </div>
        </div>

        <div className="w-px bg-surface-200" />

        {/* Right: Steps */}
        <div className="flex flex-col w-[420px] flex-shrink-0">
          <StepItem
            completed={apiKeys}
            title="Retrieve Your API Key"
            description="Get an API Key to start using the Trackstar API"
          />
          <StepItem
            completed={sandboxGenerated}
            title="Generate Sandbox Environment"
            description="Take Trackstar for a spin"
          />
          <StepItem
            completed={linkSetup}
            title="Setup Trackstar Link"
            description="Let users integrate their WMS in your UI"
          />
          <StepItem
            completed={prodApiCall}
            title="Make Production API Call"
            description="Unlock customer WMS data in your application"
          />
        </div>
      </div>

      {/* What unlocks once data flows */}
      <div className="card p-5 flex flex-col flex-1 min-h-0">
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-surface-800">What you&apos;ll see here</h2>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-4">
          {[
            {
              title: 'API traffic',
              body: 'Every request, broken down by status code over the last 24 hours.',
              path: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
            },
            {
              title: 'Connection growth',
              body: 'How many of your customers are connected, trending over time.',
              path: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
            },
            {
              title: 'Live activity',
              body: 'A feed of installs, syncs, and errors as they happen across your connections.',
              path: 'M13 10V3L4 14h7v7l9-11h-7z',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-lg bg-surface-50 border border-surface-100 p-4 flex flex-col">
              <div className="w-8 h-8 rounded-lg bg-surface-0 border border-surface-200 flex items-center justify-center mb-3">
                <svg className="w-4 h-4 text-surface-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.path} />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-surface-800 mb-1">{item.title}</h3>
              <p className="text-xs text-surface-500 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HomepageWelcome;
